import React from "react";
import { useTranslation } from "react-i18next";

import { BicycleSpecKey } from "../../../../commons/specs/bicycle";
import { ActiveFilters, ProductFilterValues } from "../../../../commons/specs/filters";
import {
  gearQualityTiers,
  gearUseCases,
  getGearBrandKeyForSeries,
  getGearSeriesMatrix,
  getUnplacedGearSeriesKeys,
  type GearBrandKey,
  type GearQualityTier,
  type GearSeriesKey,
  type GearSeriesMatrix as GearSeriesMatrixDefinition,
  type GearUseCase
} from "../../../../commons/specs/gear";
import { ProductType } from "../../../../commons/specs/product";
import { IconSmallAttention } from "../../../../resources/icons";
import CheckboxListItem from "../../components/CheckboxListItem/CheckboxListItem";
import CheckboxListItemWithImage from "../../components/CheckboxListItemWithImage/CheckboxListItemWithImage";
import FilterModalValues from "../../components/FilterModal/FilterModalValues";
import FloatingStatusBar from "../../components/FloatingStatusBar/FloatingStatusBar";
import GearBrandHeading from "../../components/GearBrandHeading/GearBrandHeading";
import GearSeriesMatrix, {
  GearSeriesMatrixColumn,
  GearSeriesMatrixRow,
  gearUseCaseIcons
} from "../../components/GearSeriesMatrix/GearSeriesMatrix";
import Headline from "../../components/Headline/Headline";
import Icon from "../../components/Icon/Icon";
import Note from "../../components/Note/Note";
import {
  addActiveFilterValue,
  getActiveFilterValues,
  isActiveFilter,
  removeActiveFilterValue
} from "../../libs/filters";
import { getGearBrandLogo } from "../../libs/gear-brand-logos";

import { removeGearBrandActiveFilters, sortGearBrandFilterValues } from "./gear-filter";
import { GearSeriesGroup, useGearSeriesGroups } from "./useGearSeriesGroups";

interface Props {
  activeFilters: ActiveFilters<ProductType.Bicycle>;
  filterValues: ProductFilterValues<ProductType.Bicycle>;
  onFilterChange: (activeFilters: ActiveFilters<ProductType.Bicycle>) => void;
}

const GearSeriesFilterModalPartial = ({ activeFilters, filterValues, onFilterChange }: Props) => {
  const { t } = useTranslation(["commons"]);
  const brandFilterValues = filterValues[BicycleSpecKey.GearBrandKey] ?? [];
  const sortedBrandFilterValues = sortGearBrandFilterValues(brandFilterValues);
  const seriesFilterValues = filterValues[BicycleSpecKey.GearSeriesKey] ?? [];
  const groups = useGearSeriesGroups(seriesFilterValues);
  const groupsByBrand = new Map(groups.map(group => [group.brandKey, group]));
  const brandLabels = new Map(sortedBrandFilterValues.map(({ value, label }) => [String(value), label]));
  const selectedBrandKeys = sortGearBrandFilterValues(
    (getActiveFilterValues(activeFilters, BicycleSpecKey.GearBrandKey) ?? []).map(value => ({
      value: String(value),
      label: brandLabels.get(String(value)) ?? String(value)
    }))
  ).map(({ value }) => value);
  const selectedSeriesKeys = getActiveFilterValues(activeFilters, BicycleSpecKey.GearSeriesKey) ?? [];
  const brandsWithSelectedSeries = new Set(selectedSeriesKeys.map(getGearBrandKeyForSeries));
  const brandLabelsWithoutSelectedSeries = selectedBrandKeys
    .filter(brandKey => !brandsWithSelectedSeries.has(brandKey as GearBrandKey))
    .map(brandKey => brandLabels.get(brandKey) ?? brandKey);
  const showAndSemanticsHint =
    selectedBrandKeys.length >= 2 && brandsWithSelectedSeries.size > 0 && brandLabelsWithoutSelectedSeries.length > 0;

  const columns: GearSeriesMatrixColumn[] = [
    {
      key: "entryMid",
      label: t("commons:filterModalPartialProductFinder.gear.qualityTierEntryMidLabel"),
      subtitle: t("commons:filterModalPartialProductFinder.gear.qualityTierEntryMidSubtitle")
    },
    {
      key: "upper",
      label: t("commons:filterModalPartialProductFinder.gear.qualityTierUpperLabel"),
      subtitle: t("commons:filterModalPartialProductFinder.gear.qualityTierUpperSubtitle")
    },
    {
      key: "highEnd",
      label: t("commons:filterModalPartialProductFinder.gear.qualityTierHighEndLabel"),
      subtitle: t("commons:filterModalPartialProductFinder.gear.qualityTierHighEndSubtitle")
    }
  ];

  const useCaseLabels = {
    mountain: t("commons:filterModalPartialProductFinder.gear.useCaseMountainLabel"),
    road: t("commons:filterModalPartialProductFinder.gear.useCaseRoadLabel"),
    gravel: t("commons:filterModalPartialProductFinder.gear.useCaseGravelLabel"),
    city: t("commons:filterModalPartialProductFinder.gear.useCaseCityLabel")
  };

  const getRowBase = (useCase: GearUseCase) => ({
    key: useCase,
    icon: gearUseCaseIcons[useCase],
    label: useCaseLabels[useCase]
  });

  const toggleSeries = (value: string) => {
    const filterKey = BicycleSpecKey.GearSeriesKey;
    onFilterChange(
      isActiveFilter(activeFilters, filterKey, value)
        ? removeActiveFilterValue(activeFilters, filterKey, value)
        : addActiveFilterValue(activeFilters, filterKey, value)
    );
  };

  const toggleBrand = (brandKey: string) => {
    const filterKey = BicycleSpecKey.GearBrandKey;
    const isActive = isActiveFilter(activeFilters, filterKey, brandKey);
    onFilterChange(
      isActive
        ? removeGearBrandActiveFilters(activeFilters, brandKey)
        : addActiveFilterValue(activeFilters, filterKey, brandKey)
    );
  };

  const getMatrixRows = (brandKey: GearBrandKey, matrix: GearSeriesMatrixDefinition): GearSeriesMatrixRow[] => {
    const group = groupsByBrand.get(brandKey);
    const availableLabels = new Map(group?.items.map(item => [String(item.value), item.label]) ?? []);

    return gearUseCases.flatMap(useCase => {
      const tierSeries: GearSeriesMatrixDefinition[typeof useCase] = matrix[useCase];
      const hasAvailableSeries = gearQualityTiers.some(tier =>
        (tierSeries?.[tier] ?? []).some(seriesKey => availableLabels.has(seriesKey))
      );
      if (!tierSeries || !hasAvailableSeries) {
        return [];
      }

      const cells = Object.fromEntries(
        gearQualityTiers.map(tier => [
          tier,
          (tierSeries[tier] ?? []).map(seriesKey => ({
            value: seriesKey,
            label: availableLabels.get(seriesKey) ?? seriesKey.replace(`${brandKey}_`, "").replaceAll("_", " "),
            active: isActiveFilter(activeFilters, BicycleSpecKey.GearSeriesKey, seriesKey),
            disabled: !availableLabels.has(seriesKey)
          }))
        ])
      ) as Record<GearQualityTier, GearSeriesMatrixRow["cells"][GearQualityTier]>;

      return [{ ...getRowBase(useCase), cells }];
    });
  };

  const emptyRows: GearSeriesMatrixRow[] = gearUseCases.map(useCase => ({
    ...getRowBase(useCase),
    cells: { entryMid: [], upper: [], highEnd: [] }
  }));

  const renderSeriesList = (items: GearSeriesGroup["items"], title?: string) => (
    <FilterModalValues title={title} justifyContent="start">
      {items.map(({ value, label }) => (
        <CheckboxListItem
          key={`${BicycleSpecKey.GearSeriesKey}-${value}`}
          outline
          label={label}
          active={isActiveFilter(activeFilters, BicycleSpecKey.GearSeriesKey, value)}
          onClick={() => toggleSeries(String(value))}
        />
      ))}
    </FilterModalValues>
  );

  return (
    <>
      <FilterModalValues
        title={t("commons:filterModalPartialProductFinder.gear.brandSectionHeadline")}
        justifyContent="start"
        layout={{ type: "responsive", minItemWidth: 140, minColumnCount: 4 }}
      >
        {sortedBrandFilterValues.map(({ value, label }) => (
          <CheckboxListItemWithImage
            key={`${BicycleSpecKey.GearBrandKey}-${value}`}
            label={label}
            imageUrl={getGearBrandLogo(String(value))}
            imageFallback={label}
            imageWidth="s"
            imageAspectRatio={21 / 9}
            size="s"
            outline
            active={isActiveFilter(activeFilters, BicycleSpecKey.GearBrandKey, value)}
            onClick={() => toggleBrand(String(value))}
          />
        ))}
      </FilterModalValues>

      <Headline kind="minor">{t("commons:filterModalPartialProductFinder.gear.seriesSectionHeadline")}</Headline>

      {selectedBrandKeys.length === 0 ? (
        <GearSeriesMatrix
          columns={columns}
          rows={emptyRows}
          decorative
          decorativeHint={t("commons:filterModalPartialProductFinder.gear.emptyStateHint")}
        />
      ) : (
        selectedBrandKeys.map(selectedBrandKey => {
          const brandKey = String(selectedBrandKey) as GearBrandKey;
          const group = groupsByBrand.get(brandKey);
          const matrix = getGearSeriesMatrix(brandKey);
          const matrixRows = matrix ? getMatrixRows(brandKey, matrix) : [];
          const unplacedSeries = new Set<GearSeriesKey>(getUnplacedGearSeriesKeys(brandKey));
          const flatItems = matrix
            ? (group?.items.filter(item => unplacedSeries.has(item.value as GearSeriesKey)) ?? [])
            : (group?.items ?? []);
          const brandHeading = (
            <GearBrandHeading label={group?.brandLabel ?? brandKey} logoUrl={getGearBrandLogo(brandKey)} />
          );

          return (
            <React.Fragment key={brandKey}>
              {matrix ? (
                <GearSeriesMatrix header={brandHeading} columns={columns} rows={matrixRows} onChange={toggleSeries} />
              ) : (
                <>
                  {brandHeading}
                  {renderSeriesList(flatItems)}
                </>
              )}

              {matrix &&
                flatItems.length > 0 &&
                renderSeriesList(flatItems, t("commons:filterModalPartialProductFinder.gear.moreSeriesHeadline"))}
            </React.Fragment>
          );
        })
      )}

      {showAndSemanticsHint && (
        <FloatingStatusBar>
          <Note
            kind="warning"
            icon={<Icon source={IconSmallAttention} />}
            spacing="s"
            verticalAlignment="center"
            withShadow
            rounding="l"
          >
            {t("commons:filterModalPartialProductFinder.gear.andSemanticsHint", {
              brands: brandLabelsWithoutSelectedSeries.join(", "),
              count: brandLabelsWithoutSelectedSeries.length
            })}
          </Note>
        </FloatingStatusBar>
      )}
    </>
  );
};

export default GearSeriesFilterModalPartial;
